/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference } from 'app/shared/models/essette-types';
import { deepClone, forceNull } from 'app/shared/lib/utilities';
import { validatePostalCode } from 'app/shared/lib/validation/validation-utilities';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class SaveAlternateAddress extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected address: CD.MemberAddress | null = null;

    protected editedAddress: CD.MemberAddress | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected types: ReadonlyArray<Core.AddressType> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected regions: ReadonlyArray<Core.AddressRegion> = [];

    protected maxTextLength: number = 50;
    protected maxCityLength: number = 30;

    protected validatePostalCode = validatePostalCode;


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {

        if(this.address) {
            this.editedAddress = deepClone(this.address);
        } else {
            this.editedAddress = null;
        }
    }


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        if(this.address) {
            this.editedAddress = deepClone(this.address);
        }
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(await this.saveAddress()) {
            this.submittedChanges = this.editedAddress;
            return true;
        }
        return false;
    }

    protected buildAddress(): CD.MemberAddress {
        const address: CD.MemberAddress = {
            id: this.editedAddress?.id,
            type: getReference(this.editedAddress?.type),
            description: forceNull(this.editedAddress?.description),
            street1: this.editedAddress?.street1,
            street2: forceNull(this.editedAddress?.street2),
            region: getReference(this.editedAddress?.region),
            city: this.editedAddress?.city,
            postalCode: this.editedAddress?.postalCode,
            member: { id: this.memberId }
        };

        return address;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveAddress(): Promise<boolean> {
        if(!this.editedAddress) return false;

        try {
            this.state.beginSave();

            const response = await this.memberService.saveMemberAddress(this.buildAddress());
            this.toaster.standard.saved('cd:memberContact.address.alternate.$', response.isSuccessful);
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('cd:memberContact.address.alternate.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
